import _ from 'lodash';

import { ANNOTATION_TYPE } from '@/shared/ui';
import { getClosestChannelIndex } from '@/shared/ui/components/FabricJS/Helper';

import { HorizontalCaliper } from './Components/horizontalCaliper';
import { VerticalCaliper } from './Components/verticalCaliper';
import { RectangleSelection } from './Components/rectangleSelection';
import { TextAnnotation } from './Components/textAnnotation';
import { BeatComplexCalipers } from './Components/beatComplexCalipers';
import { EventPosition } from './Components/eventPosition';
import { NoiseEvent } from './Components/noiseEvent';
import { PickDateIndicator } from './Components/pickDateIndicator';
import { EventIndicator } from './Components/eventIndicator';
import { BeatIndicator } from './Components/beatIndicator';
import { BeatSelection } from './Components/beatSelection';
import { BookmarkIndicator } from './Components/bookmarkIndicator';

type AnnotationsCtor = new (
  canvas: fabric.Canvas,
  config: any,
  verticalChannelPlacement: number[],
) => any;

let AnnotationsImpl: any;

(function () {
  AnnotationsImpl = function (
    canvas: fabric.Canvas,
    config: any,
    verticalChannelPlacement: number[],
  ) {
    this.annoArr = [];
    this.canvas = canvas;
    this.config = config;
    this.verticalChannelPlacement = verticalChannelPlacement;
    this.selectedObj = null;
    this.isModifying = false;
    this.pickDateIndicator = null;
    this.beatIndicator = null;
    this.beatSelection = null;
  };

  AnnotationsImpl.prototype.draw = function (
    offsetX: number,
    channelIndex: number,
    type: string,
    p: { x: number; y: number },
    bccObj: any,
  ) {
    switch (type) {
      case ANNOTATION_TYPE.HLINE: {
        const hLine = new HorizontalCaliper(
          this.canvas,
          { x1: p.x, x2: p.x, y: p.y },
          offsetX,
          this.config,
          this.verticalChannelPlacement,
          channelIndex,
        );
        this.annoArr.push(hLine);
        if (this.selectedObj) {
          this.selectedObj.deselect();
          this.selectedObj = null;
        }
        this.selectedObj = hLine;
        hLine.draw();
        hLine.select();
        this.prevPoint = p;
        break;
      }

      case ANNOTATION_TYPE.RECT_SELECT: {
        const rectSelect = new RectangleSelection(
          this.canvas,
          {
            x1: p.x,
            x2: p.x,
            y1: p.y,
            y2: p.y,
          },
          offsetX,
          this.config,
          this.verticalChannelPlacement,
          channelIndex,
        );
        this.annoArr.push(rectSelect);
        this.selectedObj = rectSelect;
        rectSelect.draw();
        this.prevPoint = p;
        break;
      }

      case ANNOTATION_TYPE.VLINE: {
        const vLine = new VerticalCaliper(
          this.canvas,
          { x: p.x, y1: p.y, y2: p.y },
          offsetX,
          this.config,
          this.verticalChannelPlacement,
          channelIndex,
        );
        this.annoArr.push(vLine);
        if (this.selectedObj) {
          this.selectedObj.deselect();
          this.selectedObj = null;
        }
        this.selectedObj = vLine;
        vLine.draw();
        vLine.select();
        this.prevPoint = p;
        break;
      }

      case ANNOTATION_TYPE.TEXT: {
        const textAnno = new TextAnnotation(
          this.canvas,
          { x: p.x, y: p.y },
          offsetX,
          this.config,
          this.verticalChannelPlacement,
          channelIndex,
        );
        this.annoArr.push(textAnno);
        if (this.selectedObj) {
          this.selectedObj.deselect();
          this.selectedObj = null;
        }
        this.selectedObj = textAnno;
        textAnno.draw();
        textAnno.select();
        break;
      }

      case ANNOTATION_TYPE.BCC: {
        const bcc = new BeatComplexCalipers(
          this.canvas,
          bccObj,
          offsetX,
          this.config,
          this.verticalChannelPlacement,
          channelIndex,
        );
        this.annoArr.push(bcc);
        if (this.selectedObj) {
          this.selectedObj.deselect();
          this.selectedObj = null;
        }
        bcc.draw();
        break;
      }

      default: {
        console.log('Invalid type');
        break;
      }
    }
    this.render();
  };

  AnnotationsImpl.prototype.drawBeatPosition = function (sampleIndex: number, offsetX: number) {
    const currentBeatPos = this.annoArr.find(
      (x: any) => x.type === ANNOTATION_TYPE.EVENT_POSITION && x.isBeatPos,
    );
    if (currentBeatPos) {
      return;
    }
    const beatPos = new EventPosition({
      canvas: this.canvas,
      startPos: sampleIndex,
      offsetX,
      config: this.config,
      isBeatPos: true,
    });
    this.annoArr.push(beatPos);
    beatPos.draw();
    this.render();
  };

  AnnotationsImpl.prototype.drawEventsPosition = function (events: any[], offsetX: number) {
    _.forEach(events, (event: any) => {
      const currentEventPos = this.annoArr.find(
        (x: any) =>
          x.type === ANNOTATION_TYPE.EVENT_POSITION && !x.isBeatPos && _.isEqual(x.event, event),
      );
      if (currentEventPos) {
        return;
      }
      const eventPos = new EventPosition({
        canvas: this.canvas,
        startPos: event.startPos,
        stopPos: event.stopPos,
        offsetX,
        config: this.config,
        color: event.color,
        id: event.id,
        event,
      });
      this.annoArr.push(eventPos);
      eventPos.draw();
    });
    this.render();
  };

  AnnotationsImpl.prototype.drawBookmarkIndicators = function (
    listBookmarks: any[] = [],
    offsetX: number,
  ) {
    // remove current bookmark indicators
    _.remove(this.annoArr, (x: any) => {
      if (x.type === ANNOTATION_TYPE.BOOKMARK_INDICATOR) {
        this.canvas.remove(x.line, x.polyline);
        return true;
      }
      return false;
    });
    _.forEach(listBookmarks, (bookmark: any) => {
      const { sample, id } = bookmark;
      const currentIndicator = this.annoArr.find(
        (x: any) => x.type === ANNOTATION_TYPE.BOOKMARK_INDICATOR && x.id === id,
      );
      if (currentIndicator) {
        currentIndicator.update(sample, offsetX);
      } else {
        const eventIndicator = new BookmarkIndicator(this.canvas, sample, offsetX, this.config, id);
        this.annoArr.push(eventIndicator);
        eventIndicator.draw(sample, offsetX);
      }
    });
    if (listBookmarks.length === 0) {
      _.remove(this.annoArr, (x: any) => {
        if (x.type === ANNOTATION_TYPE.BOOKMARK_INDICATOR) {
          this.canvas.remove(x.line, x.polyline);
          return true;
        }
        return false;
      });
    }
    this.canvas.renderAll();
  };

  AnnotationsImpl.prototype.drawEventIndicator = function (
    sampleIndex: number,
    offsetX: number,
    text: string,
  ) {
    const currentIndicator = this.annoArr.find((x: any) => x.text === text);
    if (sampleIndex) {
      if (currentIndicator) {
        currentIndicator.update(sampleIndex, offsetX);
        currentIndicator.render();
      } else {
        const eventIndicator = new EventIndicator(
          this.canvas,
          sampleIndex,
          offsetX,
          this.config,
          text,
        );
        this.annoArr.push(eventIndicator);
        eventIndicator.draw(sampleIndex, offsetX);
        eventIndicator.render();
      }
    } else if (currentIndicator) {
      this.canvas.remove(currentIndicator.line, currentIndicator.rectWithText);
      _.remove(this.annoArr, (x: any) => x.text === text);
      this.canvas.renderAll();
    }
  };

  //* Beat selection
  AnnotationsImpl.prototype.drawBeatSelection = function (
    point: { x: number; y: number },
    offsetX: number,
    isDelete: boolean,
  ) {
    this.beatSelection = new BeatSelection(
      this.canvas,
      {
        x1: point.x,
        x2: point.x,
        y1: point.y,
        y2: point.y,
      },
      offsetX,
      this.config,
      isDelete,
    );
    this.beatSelection.draw();
    this.prevPoint = point;
  };

  AnnotationsImpl.prototype.scrollBeatSelection = function (offsetX: number) {
    if (this.beatSelection) {
      this.beatSelection.scroll(offsetX);
    }
  };

  AnnotationsImpl.prototype.updateBeatSelection = function (
    point: { x: number; y: number },
    isScrolling: boolean,
    leValue: number,
    isDelete: boolean,
  ) {
    if (this.beatSelection) {
      this.beatSelection.update(
        {
          dx: point.x - this.prevPoint.x,
          dy: point.y - this.prevPoint.y,
        },
        isScrolling,
        leValue,
        isDelete,
      );
      this.prevPoint = point;
    }
  };

  AnnotationsImpl.prototype.deleteBeatSelection = function () {
    if (this.beatSelection) {
      this.beatSelection.delete();
      this.beatSelection = null;
    }
  };

  //* Beat indicator
  AnnotationsImpl.prototype.drawBeatIndicator = function ({
    beatTypeNumber,
  }: {
    beatTypeNumber: number;
  }) {
    if (this.beatIndicator) {
      this.updateBeatIndicator({ beatTypeNumber });
      return;
    }
    this.beatIndicator = new BeatIndicator(this.canvas, this.config, beatTypeNumber);
    this.beatIndicator.draw();
  };

  AnnotationsImpl.prototype.updateBeatIndicator = function (objectUpdate: any) {
    if (this.beatIndicator) {
      this.beatIndicator.update(objectUpdate);
    }
  };

  AnnotationsImpl.prototype.deleteBeatIndicator = function () {
    if (this.beatIndicator) {
      this.beatIndicator.delete();
      this.beatIndicator = null;
    }
  };

  //* Line indicator
  AnnotationsImpl.prototype.drawPickDateIndicator = function (action: string) {
    // Reset when change from start stop to bookmark
    if (this.pickDateIndicator && this.pickDateIndicator.isBookmark && action !== 'BOOKMARK') {
      this.pickDateIndicator.delete();
      this.pickDateIndicator = null;
    }
    if (this.pickDateIndicator && !this.pickDateIndicator.isBookmark && action === 'BOOKMARK') {
      this.pickDateIndicator.delete();
      this.pickDateIndicator = null;
    }
    if (this.pickDateIndicator) {
      this.pickDateIndicator.update({ action });
      return;
    }
    this.pickDateIndicator = new PickDateIndicator({
      canvas: this.canvas,
      config: this.config,
      action,
    });
    this.pickDateIndicator.draw();
  };

  AnnotationsImpl.prototype.updatePickDateIndicator = function (objUpdate: any) {
    if (this.pickDateIndicator) {
      this.pickDateIndicator.update(objUpdate);
    }
  };

  AnnotationsImpl.prototype.deletePickDateIndicator = function () {
    if (this.pickDateIndicator) {
      this.pickDateIndicator.delete();
      this.pickDateIndicator = null;
    }
  };

  AnnotationsImpl.prototype.drawNoiseEvent = function (
    noiseEventsArray: any[],
    offsetX: number,
    isAppliedAI: boolean,
  ) {
    const noiseEvents = this.annoArr.filter((x: any) => x.type === ANNOTATION_TYPE.NOISE_EVENT);
    noiseEvents.forEach((noiseEvent: any) => {
      this.canvas.remove(noiseEvent.rect);
      _.remove(this.annoArr, noiseEvent);
    });

    if (isAppliedAI) {
      noiseEventsArray.forEach((event: any) => {
        const noiseEvent = new NoiseEvent(this.canvas, event, offsetX, this.config);
        this.annoArr.push(noiseEvent);
        noiseEvent.draw();
      });
    }
  };

  AnnotationsImpl.prototype.handleChangeDiagnosisLead = function (
    offsetX: number,
    noiseEventsArray: any[],
  ) {
    const noiseEvents = this.annoArr.filter((x: any) => x.type === ANNOTATION_TYPE.NOISE_EVENT);
    noiseEvents.forEach((noiseEvent: any) => {
      this.canvas.remove(noiseEvent.rect);
      _.remove(this.annoArr, noiseEvent);
    });

    noiseEventsArray.forEach((event: any) => {
      const noiseEvent = new NoiseEvent(this.canvas, event, offsetX, this.config);
      this.annoArr.push(noiseEvent);
      noiseEvent.draw();
    });
  };

  AnnotationsImpl.prototype.render = function () {
    if (this.selectedObj) {
      this.selectedObj.render();
    }
  };

  AnnotationsImpl.prototype.update = function (
    point: { x: number; y: number },
    isDrawing: boolean,
    displayedChannelsArr: number[],
    isScrolling: boolean,
    leValue: number,
    beatMarkers: number[],
    offsetX: number,
  ) {
    if (this.selectedObj) {
      switch (this.selectedObj.type) {
        case ANNOTATION_TYPE.HLINE: {
          this.selectedObj.update(
            { dx: point.x - this.prevPoint.x, dy: point.y - this.prevPoint.y },
            isDrawing,
            displayedChannelsArr,
            isScrolling,
            leValue,
            beatMarkers,
            offsetX,
          );
          break;
        }

        case ANNOTATION_TYPE.VLINE:
          this.selectedObj.update(
            { dx: point.x - this.prevPoint.x, dy: point.y - this.prevPoint.y },
            isDrawing,
            displayedChannelsArr,
          );
          break;

        case ANNOTATION_TYPE.RECT_SELECT: {
          this.selectedObj.update(
            {
              dx: point.x - this.prevPoint.x,
              dy: point.y - this.prevPoint.y,
            },
            isScrolling,
            leValue,
          );
          break;
        }

        case ANNOTATION_TYPE.TEXT: {
          if (!this.selectedObj.isEditing()) {
            this.selectedObj.update(
              { dx: point.x - this.prevPoint.x, dy: point.y - this.prevPoint.y },
              displayedChannelsArr,
            );
          }
          break;
        }

        case ANNOTATION_TYPE.BCC: {
          this.selectedObj.update(
            { dx: point.x - this.prevPoint.x, dy: point.y - this.prevPoint.y },
            displayedChannelsArr,
          );
          break;
        }

        default: {
          console.log('Error at annotation.js, function: update');
          break;
        }
      }
      this.prevPoint = point;
      this.render();
    }
  };

  AnnotationsImpl.prototype.updateConfig = function (
    config: any,
    offsetX: number,
    verticalChannelPlacement: number[],
    displayedChannelsArr: number[],
  ) {
    this.config = config;
    this.verticalChannelPlacement = verticalChannelPlacement;
    this.annoArr.forEach((e: any) => {
      e.updateConfig(this.config, offsetX, this.verticalChannelPlacement, displayedChannelsArr);
    });
  };

  AnnotationsImpl.prototype.updateChannelIndex = function (displayedChannelsArr: number[]) {
    let y: number | undefined;

    if (this.selectedObj) {
      switch (this.selectedObj.type) {
        case ANNOTATION_TYPE.HLINE:
        case ANNOTATION_TYPE.TEXT:
          y = this.selectedObj.y;
          break;

        case ANNOTATION_TYPE.VLINE:
          // *: Update channelIndex exactly as expectation
          y = this.selectedObj.y1 + (this.selectedObj.y2 - this.selectedObj.y1) / 2;
          break;

        default:
          break;
      }
    }

    if (!y) {
      return;
    }

    const channelIndex = getClosestChannelIndex(y, this.verticalChannelPlacement);
    this.selectedObj.updateChannelIdx(displayedChannelsArr[channelIndex], displayedChannelsArr);
  };

  AnnotationsImpl.prototype.scroll = function (offsetX: number, displayedChannelsArr: number[]) {
    this.annoArr.forEach((e: any) => {
      e.scroll(offsetX, displayedChannelsArr);
    });
  };

  AnnotationsImpl.prototype.selectObj = function (point: { x: number; y: number }) {
    if (this.selectedObj) {
      if (this.selectedObj.type === 'TextAnnotation' && this.selectedObj.text.text === '') {
        _.remove(this.annoArr, (x: any) => x === this.selectedObj);
      }
      if (this.selectedObj.type === ANNOTATION_TYPE.BCC) {
        this.selectedObj.isSelected(point);
      }
      this.selectedObj.deselect();
      this.selectedObj = null;
      this.isModifying = false;
    }
    for (let i = this.annoArr.length - 1; i >= 0; i -= 1) {
      if (this.annoArr[i].isVisible && this.annoArr[i].isSelected(point)) {
        if (this.selectedObj && this.selectedObj !== this.annoArr[i]) {
          this.selectedObj.deselect();
          this.selectedObj = null;
        }
        this.selectedObj = this.annoArr[i];
        this.selectedObj.select();
        this.isModifying = true;
        this.prevPoint = point;
        break;
      }
    }
  };

  AnnotationsImpl.prototype.selectObjByInstance = function (anno: any) {
    this.selectedObj = anno;
  };

  AnnotationsImpl.prototype.deleteObj = function (isNotRemoveArr: boolean) {
    if (this.selectedObj) {
      if (
        this.selectedObj.type === ANNOTATION_TYPE.HLINE ||
        this.selectedObj.type === ANNOTATION_TYPE.VLINE
      ) {
        this.canvas.remove(
          this.selectedObj.l,
          this.selectedObj.le,
          this.selectedObj.ls,
          this.selectedObj.lt,
          this.selectedObj.lt1,
        );
      } else if (this.selectedObj.type === ANNOTATION_TYPE.BCC) {
        this.selectedObj.l.forEach((l: any) => {
          this.canvas.remove(l);
        });
        this.selectedObj.labels.forEach((label: any) => {
          this.canvas.remove(label);
        });
        this.canvas.remove(this.selectedObj.text);
      } else if (this.selectedObj.type === ANNOTATION_TYPE.TEXT) {
        this.canvas.remove(this.selectedObj.text);
      } else if (this.selectedObj.type === ANNOTATION_TYPE.RECT_SELECT) {
        this.canvas.remove(this.selectedObj.rect);
      }

      if (!isNotRemoveArr) {
        _.remove(this.annoArr, (x: any) => x === this.selectedObj);
        this.selectedObj = null;
      }
    }
  };

  AnnotationsImpl.prototype.import = function (annotation: any) {
    if (!annotation) {
      console.log('Problem at annotation.js, function import: array have null object');
    } else {
      switch (annotation.type) {
        case ANNOTATION_TYPE.HLINE: {
          const hLine = new HorizontalCaliper(
            this.canvas,
            { x1: 0, x2: 0, y: 0 },
            0,
            this.config,
            this.verticalChannelPlacement,
            0,
          );
          hLine.import(annotation);
          this.annoArr.push(hLine);
          break;
        }

        case ANNOTATION_TYPE.VLINE: {
          const vLine = new VerticalCaliper(
            this.canvas,
            { x: 0, y1: 0, y2: 0 },
            0,
            this.config,
            this.verticalChannelPlacement,
            0,
          );
          vLine.import(annotation);
          this.annoArr.push(vLine);
          break;
        }

        case ANNOTATION_TYPE.TEXT: {
          const textAnno = new TextAnnotation(
            this.canvas,
            { x: 0, y: 0 },
            0,
            this.config,
            this.verticalChannelPlacement,
            0,
          );
          textAnno.import(annotation);
          this.annoArr.push(textAnno);
          break;
        }

        case ANNOTATION_TYPE.BCC: {
          const bcc = new BeatComplexCalipers(
            this.canvas,
            annotation.xr,
            0,
            this.config,
            this.verticalChannelPlacement,
            annotation.channelIndex,
          );
          bcc.import(annotation);
          this.annoArr.push(bcc);
          break;
        }

        default:
          console.log('Error annotation.js, function import: Unexpected type: ', annotation.type);
          break;
      }
    }
  };

  AnnotationsImpl.prototype.getJSONArr = function () {
    const obj: any[] = [];
    this.annoArr.forEach((e: any) => {
      if (e.getJSONObject) {
        obj.push(e.getJSONObject());
      }
    });
    return obj;
  };
})();

const Annotations: AnnotationsCtor = AnnotationsImpl;

export { Annotations };
export default { Annotations };
